import { z } from 'zod';
import {
  VIRTUAL_LINK_MAX_LENGTH,
  VIRTUAL_PASSCODE_MAX_LENGTH,
  VIRTUAL_PLATFORMS,
} from '../location/virtual-platform.js';
import { conflictDetailSchema } from '../conflicts/schemas.js';
import { tagAwardResultSchema, attendanceTagAwardSummarySchema } from '../admin/tags/schemas.js';
import { offeringTagDisplaySchema } from '../admin/tags/offering-tag-display.js';
import { bookingPaymentStorySchema } from '../admin/booking-payment-story.js';
// The SAME ceiling the event offering's authoring door enforces. Imported, never
// re-typed as a literal: a snapshot bound that drifted from the template bound
// would reopen the exact gap this closes.
import {
  MAX_CONCURRENT_BOOKINGS,
  MAX_DEPOSIT_AMOUNT,
  MAX_DESCRIPTION_LENGTH,
  MAX_EVENT_BUFFER_MINUTES,
  MAX_REMINDER_MINUTES_BEFORE,
  MAX_SUMMARY_LENGTH,
} from '../service-admin/schemas.js';

// ============================================================================
// Phase 5 Domain 3 Feature 2.5: Event Instance Publishing Schemas
// ============================================================================

// === Instance Status Schema ===

/**
 * Event instance status enum.
 * - DRAFT: Admin-visible only, not bookable (unpublished)
 * - CONFIRMED: Live, bookable by clients
 * - CANCELLED: Instance cancelled
 *
 * Note: PENDING is excluded - it's reserved for booking/hold pipeline semantics.
 */
export const instanceStatusSchema = z.enum(['DRAFT', 'CONFIRMED', 'CANCELLED']);

// === Snapshot Sub-Schemas ===

/**
 * Form configuration for event instance snapshot.
 * Captures which forms are required and their reuse windows.
 */
export const eventInstanceFormConfigSchema = z.object({
  formId: z.string(),
  isRequired: z.boolean(),
  // event-config-authority: the reuse policy lives on the ServiceForm ATTACHMENT
  // row, so detaching a form from the template used to erase it. Frozen here so
  // an occurrence already on sale keeps the policy it was published with.
  // .optional() for backward compat: older snapshots lack it and fall back to
  // the live attachment (see `applyFrozenForms`).
  allowReuse: z.boolean().optional(),
  reuseWindowDays: z.number().int().positive().nullable(),
  sortOrder: z.number().int(),
});

/**
 * Reminder configuration for event instance snapshot.
 * isEnabled defaults to true for forward compatibility.
 */
export const eventInstanceReminderConfigSchema = z.object({
  logicalSlotKey: z.string().regex(/^(startingSoon|headsUp|extra:[A-Za-z0-9:_-]+)$/).optional(),
  // Bounded like the offering door's reminder (`validation-wave` 2026-08-26).
  // Safe on the READ path too: the snapshot is copied FROM a template whose own
  // door already enforced this same ceiling, so no stored row can exceed it.
  minutesBefore: z.number().int().positive().max(MAX_REMINDER_MINUTES_BEFORE),
  message: z.string().nullable(),
  includePrepNotes: z.boolean().default(false),
  // D-04 (2026-08-05): no editor offers a chat button any more — that channel
  // belongs to Chat Relay. An omitted field must never acquire one, so this
  // per-instance override door is fail-closed like the service-admin wire.
  includeChatButton: z.boolean().default(false),
  isEnabled: z.boolean().default(true),
});

/**
 * Follow-up configuration for event instance snapshot.
 * Uses superRefine to enforce:
 * - an ENABLED FORM entry requires a non-null formId (form-linked-to: an OFF
 *   entry whose form was archived keeps its type and message with no form)
 * - MESSAGE and GOOGLE_REVIEW types require null formId
 */
export const eventInstanceFollowUpConfigSchema = z
  .object({
    logicalSlotKey: z.string().regex(/^(aftercare|laterCheckIn|extra:[A-Za-z0-9:_-]+)$/).optional(),
    minutesAfter: z.number().int().positive(),
    type: z.enum(['MESSAGE', 'FORM', 'GOOGLE_REVIEW']),
    message: z.string().nullable(),
    formId: z.string().nullable(),
    // D-04 (2026-08-05): fail-closed — see reminder config above.
    includeChatButton: z.boolean().default(false),
    isEnabled: z.boolean().default(true),
    // form-linked-to: set when the entry's form was archived; the entry is then
    // off with this name as its visible reason until a form is attached again.
    archivedFormName: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'FORM') {
      if (data.formId === null && data.isEnabled) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'formId is required when type is FORM and the entry is enabled',
          path: ['formId'],
        });
      }
    } else {
      // MESSAGE or GOOGLE_REVIEW - formId must be null
      if (data.formId !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `formId must be null when type is ${data.type}`,
          path: ['formId'],
        });
      }
    }
  });

// === Main Config Snapshot Schema ===

/**
 * Event instance configuration snapshot.
 * This is the "frozen" booking contract copied from the template service at schedule time.
 * Stored as JSONB in EventInstanceConfig.configSnapshot.
 *
 * `event-instance-editor` trimmed four fields off this contract, and the absences
 * are rules, not omissions:
 * - `minimumNoticeMinutes` / `advanceBookingDays` — session-only booking-window
 *   terms. Ruling D-10: an event closes on its OWN `bookingCutoffMinutes` and
 *   nothing else, so freezing the tenant-wide notice chain per occurrence only
 *   invited two gates to disagree.
 * - `broadcastConfig` — a Domain 10 placeholder no writer ever set to anything
 *   but `null` and no reader ever consulted.
 * - `includeNotesInAfterAction` — the after-action notes step is gone and its
 *   offering column was dropped (approval-prompt-resolves D6).
 *
 * Zod strips unknown keys, so this object is ALSO the normalizer: legacy stored
 * JSON carrying the removed keys loses them on the way to any reader that parses
 * through here (instance detail via `normalizeConfigSnapshot`, MANUAL_DATED
 * series detail). Raw JSON is canonicalized lazily — the next accepted config
 * write rewrites it, a config-less write leaves it alone.
 */
export const eventInstanceConfigSnapshotSchema = z.object({
  // Capacity. Ceiling mirrors the offering door (`validation-wave` 2026-08-26).
  maxConcurrentBookings: z.number().int().positive().max(MAX_CONCURRENT_BOOKINGS),

  // Pricing (Decimal converted to number in JSON). A negative price used to be
  // accepted here AND is exempt from the booked-occurrence lock, so it could be
  // written onto an occurrence already on sale.
  price: z.number().nonnegative().nullable(),
  duration: z.number().int().positive(),

  // Payment rules
  paymentRequirement: z.enum(['NONE', 'PAY_AT_SESSION', 'DEPOSIT', 'FULL_PAYMENT']),
  depositAmount: z.number().nonnegative().max(MAX_DEPOSIT_AMOUNT).nullable(),
  depositType: z.enum(['FIXED', 'PERCENTAGE']).nullable(),

  // Booking gates
  requiresApproval: z.boolean(),
  allowInvites: z.boolean(),
  // Ruling D-10: an event closes on its OWN cutoff, never the tenant-wide notice
  // chain — so this is the gate the hold doors actually read, and it has to be
  // frozen or a template edit reopens/closes an occurrence already on sale.
  // .optional() for backward compat: snapshots written before this field lack
  // it and fall back to the template (see `resolveEventBookingCutoffMinutes`).
  bookingCutoffMinutes: z.number().int().nonnegative().nullable().optional(),

  /**
   * occurrence-boundary (2026-08-27): the cancellation terms this date was sold
   * under.
   *
   * Read live off the template on the client event page, four lines after the
   * frozen terms were resolved — so a client who booked under a flexible policy
   * later saw a strict one. Nothing enforces the policy in code today, which
   * makes this a false PROMISE rather than a wrong refund; freezing it now means
   * the enforcement that eventually lands reads the agreed terms rather than
   * whatever the template says that day.
   *
   * `.optional()` for backward compat: a snapshot written before this field
   * lacks it and falls back to the template, which is exactly today's behavior.
   */
  cancellationPolicy: z.string().nullable().optional(),

  /**
   * booking-truth-batch (2026-10-08): the CUSTOM policy's minutes, frozen with
   * the policy so the enforced window is the one the date was sold under.
   * `.optional()` for snapshots written before the key existed (template fallback).
   */
  customCancellationCutoffMinutes: z.number().int().nonnegative().nullable().optional(),

  /**
   * occurrence-boundary (2026-08-27): the payment rails this date was sold with.
   *
   * Bootstrap, card, Venmo and both post-approval doors enforced the CURRENT
   * `Service.acceptedPaymentMethods`, so an admin removing a rail could strand a
   * client mid-checkout on an occurrence already scheduled — or already
   * approved. Only the offering's CHOICE freezes; tenant payment-account HEALTH
   * (Stripe charges-enabled, Venmo handle present) stays a live read, because a
   * disconnected account genuinely cannot take money no matter what was agreed.
   */
  acceptedPaymentMethods: z.array(z.string()).optional(),

  // Forms
  forms: z.array(eventInstanceFormConfigSchema),

  // Notifications
  reminders: z.array(eventInstanceReminderConfigSchema),
  followUps: z.array(eventInstanceFollowUpConfigSchema),

  // Buffer times (Phase 5 Domain 5 — captured from Service template at snapshot time)
  // .optional() for backward compat: old snapshots lack these fields
  //
  // CEILING ENFORCED HERE, and it is load-bearing beyond tidiness.
  //
  // `resolveBuffers` (calendar-runtime/utilities/buffer-resolution.ts) reads the
  // SNAPSHOT FIRST for event and recurring_event, falling back to the Service row
  // only when the snapshot has no value. The Service row is already bounded at
  // the authoring door (`MAX_EVENT_BUFFER_MINUTES` on the event offering schema,
  // `MAX_SERVICE_BUFFER_MINUTES` via the session superRefine), but the snapshot
  // was not — so an override could set a 780-minute buffer that WINS over the
  // bounded template value.
  //
  // That is not just an odd number in a column: every consumer that widens a
  // calendar fetch range to catch conflicting items' buffers has to size that
  // widening by some finite constant. An unbounded snapshot makes every such
  // constant wrong, silently, by missing conflicts that lie outside the window.
  //
  // Bounding the base schema closes ALL FOUR write doors at once, because each
  // derives from it: `scheduleInstanceRequest.configOverrides`,
  // `updateInstanceRequest.configSnapshot`, and `wizardOverrides` on both series
  // request variants.
  bufferBeforeMinutes: z.number().int().nonnegative().max(MAX_EVENT_BUFFER_MINUTES).nullable().optional(),
  bufferAfterMinutes: z.number().int().nonnegative().max(MAX_EVENT_BUFFER_MINUTES).nullable().optional(),
});

// === API Request Schemas ===

/**
 * Publish mode for scheduling instances.
 * - 'draft': Creates as DRAFT (not bookable, admin-visible only)
 * - 'live': Creates as CONFIRMED with publishedAt set (immediately bookable)
 */
export const publishModeSchema = z.enum(['draft', 'live']).default('draft');

/**
 * Schedule instance request schema.
 * Creates a new event instance from a template service.
 *
 * Note: All datetime fields use ISO 8601 format (z.string().datetime()).
 *
 * @param publishMode - 'draft' (default) or 'live' for atomic create+publish
 */
export const scheduleInstanceRequestSchema = z.object({
  templateServiceId: z.string(),
  /** Optional: defaults to authenticated user's ID in single-admin scenarios */
  practitionerId: z.string().optional(),
  startsAt: z.string().datetime({ offset: true }),
  /** Optional: defaults to startsAt + template.duration */
  endsAt: z.string().datetime({ offset: true }).optional(),

  /**
   * Publish mode determines initial status:
   * - 'draft' (default): Creates as DRAFT, publishedAt=null
   * - 'live': Creates as CONFIRMED, publishedAt=now() (atomic create+publish)
   */
  publishMode: publishModeSchema,

  /**
   * When true with publishMode='draft', persisted conflict records get
   * autoPublishEligible=true, signaling F5's auto-publish worker.
   * Set to true when retrying after 409 CONFLICTS_DETECTED.
   */
  autoPublishOnConflictClear: z.boolean().optional().default(false),

  /**
   * schedule-event-redesign: the composer's proof of WHICH template structure it
   * composed against (`computeTemplateStructureToken`). Verified under the
   * template write lock; a mismatch is `STALE_TEMPLATE` (409).
   *
   * Presence also opts the request into the composer's mode-structure matrix —
   * on this door, a continuous multi-day template's explicit `endsAt` must land
   * on the day the CURRENT `eventDaysCount` dictates. Absent means a
   * non-composer caller and none of that applies.
   */
  templateStructureToken: z.string().min(1).optional(),

  // Optional presentation overrides. Bounds match the update door below and the
  // offering door they are copied from (`validation-wave` 2026-08-26).
  title: z.string().trim().min(1).optional(),
  description: z.string().max(MAX_DESCRIPTION_LENGTH).nullable().optional(),
  customImage: z.string().nullable().optional(),
  focalPointX: z.number().min(0).max(100).nullable().optional(),
  focalPointY: z.number().min(0).max(100).nullable().optional(),
  // Mirrors the offering door's bounds. Added 2026-08-29: the door accepted a
  // custom cover with its own X/Y but no scale, then stamped the TEMPLATE's
  // zoom onto it — geometry describing a different picture.
  focalPointScale: z.number().min(1).max(3).nullable().optional(),

  // Optional location overrides (includes placeId for Google Places)
  locationName: z.string().nullable().optional(),
  locationAddress: z.string().nullable().optional(),
  locationCity: z.string().nullable().optional(),
  locationState: z.string().nullable().optional(),
  locationCountry: z.string().nullable().optional(),
  locationLat: z.number().nullable().optional(),
  locationLng: z.number().nullable().optional(),
  locationPlaceId: z.string().nullable().optional(),
  locationNotes: z.string().nullable().optional(),

  // Optional virtual overrides: the date's own REPLACEMENT triple
  // (virtual-sessions-finish). Shape here; the shared link rule in replacement
  // mode runs in the service. Omitted means the date follows its template.
  isVirtual: z.boolean().optional(),
  virtualPlatform: z.enum(VIRTUAL_PLATFORMS).nullable().optional(),
  virtualLink: z.string().max(VIRTUAL_LINK_MAX_LENGTH).nullable().optional(),
  virtualPassword: z.string().max(VIRTUAL_PASSCODE_MAX_LENGTH).nullable().optional(),

  // Optional config overrides (merged into snapshot from template)
  configOverrides: eventInstanceConfigSnapshotSchema.partial().optional(),
});

/**
 * Update instance request schema.
 * Partial update - editing rules enforced by service layer based on status/bookings.
 */
export const updateInstanceRequestSchema = z.object({
  // Presentation fields (always editable)
  /**
   * REFUSED when empty (`validation-wave` 2026-08-26). Its two text neighbours
   * map `''` to null and fall back to the blueprint's copy on read; a title has
   * nothing to fall back to, so an empty one was simply stored and an untitled
   * occurrence rendered blank in the catalog, the event page and every reminder.
   */
  title: z.string().trim().min(1).optional(),
  /**
   * The occurrence's own one-line catalog blurb (2026-08-26). Bounded by the
   * SAME constant the offering door enforces on `Service.summary` — imported,
   * never re-typed, so the two doors cannot drift apart. Trimmed before the
   * bound, matching that door, so trailing whitespace can never spend the cap.
   */
  summary: z.string().trim().max(MAX_SUMMARY_LENGTH).nullable().optional(),
  /**
   * The occurrence's own preparation notes (2026-08-27). Rich HTML, SANITIZED
   * by the service through the offering door's own `sanitizePreparationNotes` —
   * both columns feed the same SafeRichText render, so a door that stored what
   * its sibling would have cleaned is a stored-XSS gap with extra steps.
   *
   * Unbounded here for the same reason the offering door leaves it unbounded:
   * the sanitizer, not a length, is what makes the value safe.
   */
  preparationNotes: z.string().nullable().optional(),
  description: z.string().max(MAX_DESCRIPTION_LENGTH).nullable().optional(),
  adminNotes: z.string().nullable().optional(),
  customImage: z.string().nullable().optional(),
  focalPointX: z.number().min(0).max(100).nullable().optional(),
  focalPointY: z.number().min(0).max(100).nullable().optional(),
  // Same pairing as the schedule door: a cover change without a zoom change
  // leaves the old framing on a new picture.
  focalPointScale: z.number().min(1).max(3).nullable().optional(),

  // Location fields
  locationName: z.string().nullable().optional(),
  locationAddress: z.string().nullable().optional(),
  locationCity: z.string().nullable().optional(),
  locationState: z.string().nullable().optional(),
  locationCountry: z.string().nullable().optional(),
  locationLat: z.number().nullable().optional(),
  locationLng: z.number().nullable().optional(),
  locationPlaceId: z.string().nullable().optional(),
  locationNotes: z.string().nullable().optional(),

  // Virtual fields: the date's own REPLACEMENT triple (virtual-sessions-finish);
  // all three null is "Use the template link". Rule enforced in the service.
  isVirtual: z.boolean().optional(),
  virtualPlatform: z.enum(VIRTUAL_PLATFORMS).nullable().optional(),
  virtualLink: z.string().max(VIRTUAL_LINK_MAX_LENGTH).nullable().optional(),
  virtualPassword: z.string().max(VIRTUAL_PASSCODE_MAX_LENGTH).nullable().optional(),

  // Time (editable rules depend on status + bookings - enforced in service)
  startsAt: z.string().datetime().optional(),
  endsAt: z.string().datetime().optional(),

  // Config snapshot fields (partial update - rules enforced in service)
  configSnapshot: eventInstanceConfigSnapshotSchema.partial().optional(),
})
  /**
   * A range that ends before it starts (`validation-wave` 2026-08-26).
   *
   * Caught here only when the PATCH names BOTH sides — that is the shape this
   * schema can judge on its own. A one-sided edit is judged in the service
   * against the STORED other side (`InvalidTimeRangeError`), because the
   * effective pair is the only thing that can be inverted and the schema cannot
   * see the stored half.
   */
  .superRefine((data, ctx) => {
    if (data.startsAt === undefined || data.endsAt === undefined) return;
    if (Date.parse(data.endsAt) > Date.parse(data.startsAt)) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'endsAt must be after startsAt',
      path: ['endsAt'],
    });
  });

// === API Response Schemas ===

/**
 * Location DTO schema for response objects.
 * Includes placeId for Google Places consistency.
 */
export const locationDtoSchema = z.object({
  name: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  notes: z.string().nullable(),
  placeId: z.string().nullable(),
});

/**
 * Event instance list item schema.
 * Summary for paginated listing with attendance stats.
 *
 * attendance.confirmed = CONFIRMED bookings only (for "X attending" display)
 * attendance.takenSeats = total capacity-consuming (for progress bar)
 */
export const eventInstanceListItemSchema = z.object({
  calendarEventId: z.string(),
  status: instanceStatusSchema,
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  title: z.string(),
  customImage: z.string().nullable(),
  attendance: z.object({
    confirmed: z.number().int(),  // CONFIRMED only
    takenSeats: z.number().int(), // confirmed + inFlight + activeHolds
    capacity: z.number().int(),
  }),
  publishedAt: z.string().datetime().nullable(),
  // Server-computed opens-policy date (events-page-redesign): startsAt minus the
  // applicable release window — template goesLiveDaysBefore for non-series
  // instances, EventSeries.releaseDaysBefore for ROLLING_DAYS_BEFORE series
  // instances. Null when no policy applies (IMMEDIATE / no goesLiveDaysBefore →
  // a hidden session stays "Hidden" until manually opened). The admin event view
  // renders the "Opens {date}" / "Hidden" pill SOLELY from this — the client
  // never re-derives policy.
  opensAt: z.string().datetime().nullable(),
});

/**
 * Event instance detail schema.
 * Full detail including config snapshot, template reference, and attendance.
 */
export const eventInstanceDetailSchema = z.object({
  calendarEventId: z.string(),
  status: instanceStatusSchema,
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  title: z.string(),
  /** Per-occurrence catalog blurb; null falls back to the template's on read. */
  summary: z.string().nullable(),
  /**
   * This occurrence's OWN preparation notes — a plain copy taken at scheduling.
   * Null means this date has NONE; nothing falls back to the template.
   */
  preparationNotes: z.string().nullable(),
  /**
   * The heading for the notes above, frozen with them (occurrence-boundary).
   * Null means "use the default heading", as a null template label does.
   */
  preparationNotesLabel: z.string().nullable(),
  description: z.string().nullable(),
  adminNotes: z.string().nullable(),
  opensAt: z.string().datetime().nullable(),
  netCollected: z.number().nonnegative(),

  // Image
  customImage: z.string().nullable(),
  focalPointX: z.number().nullable(),
  focalPointY: z.number().nullable(),
  focalPointScale: z.number().min(1).max(3).nullable().optional(),

  // Location
  location: locationDtoSchema.nullable(),
  isVirtual: z.boolean(),
  virtualPlatform: z.string().nullable(),
  virtualLink: z.string().nullable(),
  virtualPassword: z.string().nullable(),
  /**
   * The template's online delivery (virtual-sessions-finish, design D12): what
   * a single date with no link of its own resolves to. Null while the template
   * is not virtual. The date's own triple above is its replacement.
   */
  templateVirtualPlatform: z.string().nullable(),
  templateVirtualLink: z.string().nullable(),
  templateVirtualPassword: z.string().nullable(),

  // Template reference
  templateServiceId: z.string(),
  templateServiceName: z.string(),
  templateServiceImage: z.string().nullable(),

  // Config snapshot
  configSnapshot: eventInstanceConfigSnapshotSchema,

  // Attendance breakdown for admin visibility
  // - confirmed: CONFIRMED bookings only (for display)
  // - inFlight: PENDING_APPROVAL + PENDING_PAYMENT + IN_PROGRESS
  // - activeHolds: PENDING holds with active TTL
  // - takenSeats: total = confirmed + inFlight + activeHolds (for capacity validation)
  attendance: z.object({
    confirmed: z.number().int(),
    inFlight: z.number().int(),
    activeHolds: z.number().int(),
    takenSeats: z.number().int(),
    capacity: z.number().int(),
  }),

  // Accent theme variant (purple for one-off events, blue for recurring instances)
  // Derived from EventInstanceConfig.isRecurrenceGenerated — matches deriveItemType() logic
  accentVariant: z.enum(['event', 'recurring_event']),

  // Recurrence metadata — present only for a member of a RUNNING RRULE series
  // (null for a package day, a standalone occurrence, and a stopped rhythm)
  recurrence: z.object({
    isRecurrenceGenerated: z.boolean(),
    templateIsRecurring: z.boolean(),
    rrulePattern: z.string().nullable(),
    seriesStartsAt: z.string().datetime().nullable(),
    seriesEndsAt: z.string().datetime().nullable(),
    occurrenceIndex: z.number().int().nullable(),      // 1-based, null if open-ended (no COUNT)
    occurrenceTotal: z.number().int().nullable(),       // null if open-ended (no COUNT)
    nextInstances: z.array(z.object({
      calendarEventId: z.string(),
      startsAt: z.string().datetime(),
      title: z.string(),
    })).max(3),
  }).nullable(),

  // Per-form submission counts for the Forms Card
  formSubmissions: z.array(z.object({
    formId: z.string(),
    formName: z.string(),
    isRequired: z.boolean(),
    submittedCount: z.number().int(),
    totalAttendees: z.number().int(),
    anonymous: z.boolean(),
    attendees: z.array(z.object({
      bookingId: z.string(),
      userId: z.string(),
      name: z.string(),
      submissionState: z.enum(['FRESH', 'REUSED', 'PENDING']),
      submissionId: z.string().nullable(),
      submittedAt: z.string().nullable(),
    })),
  })),

  // D5.1 F1: Active and resolved conflict records for this instance
  conflicts: z.array(conflictDetailSchema),

  // P5 D8 F10: Tag display data for TagsCard
  tags: offeringTagDisplaySchema.optional(),

  // Series membership (null for one-time events)
  eventSeriesId: z.string().nullable(),
  /**
   * The series' PERMANENT type, and the discriminator every series-conditional
   * behavior on the detail page keys on. Null for a standalone occurrence.
   *
   * Deliberately not derivable from `recurrence`: that metadata is withheld for
   * a package day, a standalone occurrence AND a stopped rhythm, so its absence
   * cannot tell those three apart — and identity has to survive the rhythm
   * stopping, which is the bug that put this field here.
   */
  seriesType: z.enum(['RRULE', 'MANUAL_DATED']).nullable(),
  /** The series' own stopped state. The ONLY source for "ended" on the chip. */
  seriesStoppedAt: z.string().datetime().nullable(),
  /** MANUAL_DATED members only — day-of-N is package language (RULED 2026-08-19). */
  seriesInstanceIndex: z.number().int().positive().nullable(),
  seriesInstanceCount: z.number().int().positive().nullable(),

  // Audit
  publishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

/**
 * Publish instance response schema.
 * Returns CONFIRMED status and publishedAt timestamp.
 */
export const publishInstanceResponseSchema = z.object({
  calendarEventId: z.string(),
  status: z.literal('CONFIRMED'),
  publishedAt: z.string().datetime(),
});

/**
 * Unpublish instance response schema.
 * Returns DRAFT status and cleared publishedAt.
 */
export const unpublishInstanceResponseSchema = z.object({
  calendarEventId: z.string(),
  status: z.literal('DRAFT'),
  publishedAt: z.null(),
  /** Held seats the occurrence still holds after hiding. Bookings never block hiding. */
  bookedCount: z.number().int().nonnegative(),
});

/**
 * Pagination schema (reusable pattern from service-admin).
 */
export const instancePaginationSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});

/**
 * Paginated instances response schema.
 */
export const paginatedInstancesResponseSchema = z.object({
  data: z.array(eventInstanceListItemSchema),
  pagination: instancePaginationSchema,
});

// ============================================================================
// Phase 5 Domain 5 Feature 5: Attendee Roster & Attendance Marking Schemas
// Task: T-P5D5F5-3
// ============================================================================

/**
 * Individual attendee row in the roster.
 * Contains user info, booking status, payment, and attendance data.
 */
export const attendeeRowSchema = z.object({
  bookingId: z.string(),
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string().nullable(),
  username: z.string().nullable(),
  /** chat-relay-repoints D2: null for an external client; gates the Message door. */
  telegramId: z.string().nullable(),
  avatar: z.string().nullable(),
  bookingStatus: z.enum([
    'PENDING_APPROVAL', 'PENDING_PAYMENT', 'IN_PROGRESS',
    'CONFIRMED', 'COMPLETED', 'CANCELLED', 'NO_SHOW', 'WAITLISTED',
  ]),
  attendance: z.enum(['ATTENDED', 'NO_SHOW']).nullable(),
  attendanceMarkedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /** post-event-roster: set only on CANCELLED rows; drives the "cancelled {date}" copy. */
  cancelledAt: z.string().datetime().nullable(),
  /**
   * post-event-roster: tag names NEWLY awarded to this attendee by this event's
   * attendance marking, read from ClientTagAssignment provenance
   * (awardingCalendarEventId). Empty when the attendee already held the tag —
   * the award writer's ON CONFLICT DO NOTHING leaves no provenance row.
   */
  awardedTags: z.array(z.string()),
  ...bookingPaymentStorySchema.shape,
});

/**
 * Attendee roster response.
 * Groups attendees into confirmed/pending/waitlisted sections.
 *
 * Grouping logic:
 * - confirmed = CONFIRMED | COMPLETED | NO_SHOW | CANCELLED-with-confirmedAt
 * - pending = PENDING_APPROVAL | PENDING_PAYMENT | IN_PROGRESS
 * - waitlisted = WAITLISTED
 * - Excludes CANCELLED bookings that were never confirmed (confirmedAt null)
 * - Each group sorted by createdAt ASC
 *
 * post-event-roster: cancelled-after-confirmed rows are DISPLAY rows only —
 * they are never seat-holders or markables. `counts.confirmed` excludes them,
 * and every consumer must filter `bookingStatus === 'CANCELLED'` out of seat,
 * money, and markable-attendee derivations.
 */
export const attendeeRosterResponseSchema = z.object({
  confirmed: z.array(attendeeRowSchema),
  pending: z.array(attendeeRowSchema),
  waitlisted: z.array(attendeeRowSchema),
  counts: z.object({
    confirmed: z.number().int(),
    pending: z.number().int(),
    waitlisted: z.number().int(),
    total: z.number().int(),
  }),
});

/**
 * Request body for marking attendance on multiple attendees.
 * Pre-validates all bookings before marking — no partial results.
 */
export const markEventAttendanceRequestSchema = z.object({
  attendees: z.array(z.object({
    bookingId: z.string(),
    attendance: z.enum(['ATTENDED', 'NO_SHOW']),
  })).min(1).refine(
    (attendees) => {
      const ids = attendees.map((a) => a.bookingId);
      return new Set(ids).size === ids.length;
    },
    { message: 'Duplicate bookingId values are not allowed' },
  ),
});

/**
 * Individual result from marking attendance.
 */
export const markAttendanceResultSchema = z.object({
  bookingId: z.string(),
  attendance: z.enum(['ATTENDED', 'NO_SHOW']),
  changed: z.boolean(),
  tagAwards: z.array(tagAwardResultSchema),
});

/**
 * Response from the mark attendance endpoint.
 * Contains per-booking results and an aggregate summary.
 */
export const markEventAttendanceResponseSchema = z.object({
  results: z.array(markAttendanceResultSchema),
  summary: z.object({
    attended: z.number().int(),
    noShow: z.number().int(),
  }),
  tagAwardSummary: attendanceTagAwardSummarySchema,
});
